import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  Link2,
  Copy,
  Check,
  RefreshCw,
  MapPin,
  Save,
  Clock,
  Trash2,
  UserPlus,
  ExternalLink,
} from "lucide-react";
import { ClienteFormDialog } from "@/features/clientes/ClienteFormDialog";
import { ContratoFormDialog } from "@/features/contratos/ContratoFormDialog";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { LocationPicker } from "@/components/LocationPicker";
import {
  ESTADO_LABEL,
  ESTADO_PILL,
  ESTADOS,
  capturaUrl,
  useConvertirProspecto,
  useDeleteProspecto,
  useGenerarLink,
  useProspecto,
  useUpdateProspecto,
  type EstadoProspecto,
} from "./api";

export function ProspectoDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: p, isLoading } = useProspecto(id);
  const update = useUpdateProspecto(id);
  const genLink = useGenerarLink(id);
  const del = useDeleteProspecto();
  // rc145 — Convertir en cliente: Nuevo cliente prellenado → liga el prospecto
  // → Nuevo contrato prellenado (GPS + referencia). Mismos dos diálogos de siempre.
  const convertir = useConvertirProspecto(id);
  const [clienteOpen, setClienteOpen] = useState(false);
  const [contratoOpen, setContratoOpen] = useState(false);
  const [clienteNuevoId, setClienteNuevoId] = useState<string | null>(null);

  const [nombre, setNombre] = useState("");
  const [telefono, setTelefono] = useState("");
  const [estado, setEstado] = useState<EstadoProspecto>("nuevo");
  const [notas, setNotas] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (p) {
      setNombre(p.nombre_completo ?? "");
      setTelefono(p.telefono ?? "");
      setEstado(p.estado);
      setNotas(p.notas ?? "");
    }
  }, [p]);

  if (isLoading || !p) {
    return <div className="p-8 text-center text-muted-foreground">Cargando…</div>;
  }

  const dirty =
    nombre !== (p.nombre_completo ?? "") ||
    telefono !== (p.telefono ?? "") ||
    estado !== p.estado ||
    notas !== (p.notas ?? "");

  async function handleSave() {
    try {
      await update.mutateAsync({
        nombre_completo: nombre.trim(),
        telefono: telefono.trim(),
        estado,
        notas: notas.trim(),
      });
      toast.success("Cambios guardados");
    } catch (err) {
      toast.error((err as { message?: string })?.message ?? "No se pudo guardar");
    }
  }

  async function handleDelete() {
    if (!window.confirm(`¿Eliminar el prospecto ${p?.folio}? Esta acción no se puede deshacer.`)) return;
    try {
      await del.mutateAsync(id);
      toast.success("Prospecto eliminado");
      navigate("/prospectos");
    } catch (err) {
      toast.error((err as { message?: string })?.message ?? "No se pudo eliminar");
    }
  }

  async function handleGenerar() {
    try {
      await genLink.mutateAsync();
      toast.success("Link de captura generado");
    } catch (err) {
      toast.error((err as { message?: string })?.message ?? "No se pudo generar el link");
    }
  }

  async function copyLink() {
    if (!p?.captura_token) return;
    await navigator.clipboard.writeText(capturaUrl(p.captura_token));
    setCopied(true);
    toast.success("Link copiado. Pégalo en WhatsApp del prospecto.");
    setTimeout(() => setCopied(false), 1800);
  }

  const tokenVigente =
    !!p.captura_token && (!p.token_expira_at || new Date(p.token_expira_at) > new Date());

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/prospectos" className="rounded-md border p-1.5 hover:bg-muted">
          <ArrowLeft size={16} />
        </Link>
        <div className="flex items-center gap-2">
          <h1 className="font-mono text-lg font-semibold">{p.folio}</h1>
          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${ESTADO_PILL[p.estado]}`}>
            {ESTADO_LABEL[p.estado]}
          </span>
        </div>
        {p.estado === "convertido" && p.convertido_cliente_id ? (
          <Link
            to={`/clientes/${p.convertido_cliente_id}`}
            className="ml-auto inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm hover:bg-muted"
          >
            <ExternalLink size={15} /> Ver cliente
          </Link>
        ) : (
          <button
            type="button"
            onClick={() => setClienteOpen(true)}
            disabled={p.estado === "descartado"}
            title={p.estado === "descartado" ? "Un prospecto descartado no se convierte" : undefined}
            className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            <UserPlus size={15} /> Convertir en cliente
          </button>
        )}
        <button
          onClick={handleDelete}
          disabled={del.isPending}
          className="inline-flex items-center gap-1.5 rounded-md border border-rose-200 px-3 py-1.5 text-sm text-rose-600 hover:bg-rose-50 disabled:opacity-60 dark:border-rose-900 dark:text-rose-400 dark:hover:bg-rose-950"
        >
          {del.isPending ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
          Eliminar
        </button>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Datos */}
        <section className="rounded-lg border bg-card p-4">
          <h2 className="mb-3 text-sm font-semibold">Datos del prospecto</h2>
          <div className="flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium">Nombre completo</span>
              <input
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                className="rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium">Teléfono</span>
              <input
                value={telefono}
                onChange={(e) => setTelefono(e.target.value)}
                inputMode="tel"
                className="rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium">Estado</span>
              <select
                value={estado}
                onChange={(e) => setEstado(e.target.value as EstadoProspecto)}
                className="rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary"
              >
                {ESTADOS.map((e) => (
                  <option key={e} value={e}>{ESTADO_LABEL[e]}</option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium">Notas</span>
              <textarea
                value={notas}
                onChange={(e) => setNotas(e.target.value)}
                rows={3}
                className="resize-none rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary"
              />
            </label>
            <button
              onClick={handleSave}
              disabled={!dirty || update.isPending}
              className="inline-flex items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {update.isPending ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
              Guardar cambios
            </button>
          </div>
        </section>

        {/* Link de captura + Ubicación */}
        <div className="flex flex-col gap-4">
          <section className="rounded-lg border bg-card p-4">
            <h2 className="mb-1 flex items-center gap-2 text-sm font-semibold">
              <Link2 size={15} /> Link de captura de ubicación
            </h2>
            <p className="mb-3 text-xs text-muted-foreground">
              Genera el link y cópialo. Envíalo por WhatsApp al prospecto: desde su celular llenará
              nombre y teléfono y compartirá su ubicación con precisión.
            </p>

            {tokenVigente && p.captura_token ? (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <input
                    readOnly
                    value={capturaUrl(p.captura_token)}
                    className="flex-1 rounded-md border bg-muted/40 px-3 py-2 font-mono text-xs outline-none"
                  />
                  <button
                    onClick={copyLink}
                    className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
                  >
                    {copied ? <Check size={15} /> : <Copy size={15} />}
                    {copied ? "Copiado" : "Copiar"}
                  </button>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1">
                    <Clock size={11} />
                    {p.token_expira_at
                      ? `Expira ${new Date(p.token_expira_at).toLocaleString("es-MX")}`
                      : "Sin expiración"}
                  </span>
                  <button
                    onClick={handleGenerar}
                    disabled={genLink.isPending}
                    className="inline-flex items-center gap-1 hover:text-foreground"
                  >
                    <RefreshCw size={11} className={genLink.isPending ? "animate-spin" : ""} /> Regenerar
                  </button>
                </div>
                {p.capturado && (
                  <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                    <Check size={12} /> El prospecto ya envió sus datos.
                  </span>
                )}
              </div>
            ) : (
              <button
                onClick={handleGenerar}
                disabled={genLink.isPending}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-60"
              >
                {genLink.isPending ? <Loader2 size={15} className="animate-spin" /> : <Link2 size={15} />}
                {p.captura_token ? "Regenerar link (expiró)" : "Generar link de captura"}
              </button>
            )}
          </section>

          <section className="rounded-lg border bg-card p-4">
            <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold">
              <MapPin size={15} /> Ubicación
            </h2>
            {p.lat !== null && p.lng !== null ? (
              <div className="flex flex-col gap-2">
                <LocationPicker value={{ lat: p.lat, lng: p.lng }} onChange={() => {}} readOnly tiles="satellite" heightPx={220} />
                <div className="text-xs text-muted-foreground">
                  {p.ubicacion_precision_m != null && <>Precisión ±{p.ubicacion_precision_m} m · </>}
                  {p.ubicacion_capturada_at && (
                    <>Capturada {new Date(p.ubicacion_capturada_at).toLocaleString("es-MX")}</>
                  )}
                </div>
              </div>
            ) : (
              <p className="py-6 text-center text-sm text-muted-foreground">
                Aún sin ubicación. Genera el link y pídela al prospecto.
              </p>
            )}
          </section>
        </div>
      </div>
      {/* rc145 — Convertir en cliente */}
      <ClienteFormDialog
        open={clienteOpen}
        onOpenChange={setClienteOpen}
        initialValues={{
          tipo: "residencial",
          empresa_id: p.empresa_id ?? 1,
          razon_social: p.nombre_completo ?? "",
          notas_internas: `Prospecto ${p.folio}${p.notas ? ` · ${p.notas}` : ""}`,
          direccion_instalacion: {
            ...(p.lat != null && p.lng != null ? { latitud: p.lat, longitud: p.lng } : {}),
          },
          contactos: p.nombre_completo
            ? [{ nombre: p.nombre_completo, telefono: p.telefono ?? "", es_principal: true, recibe_avisos: true }]
            : [],
        }}
        onSaved={async (clienteId) => {
          setClienteNuevoId(clienteId);
          try {
            await convertir.mutateAsync(clienteId);
          } catch {
            toast.error("El cliente se creó, pero no se pudo ligar el prospecto.");
          }
          toast.success("Cliente creado. Ahora el contrato.");
          setContratoOpen(true);
        }}
      />
      {clienteNuevoId && (
        <ContratoFormDialog
          open={contratoOpen}
          onOpenChange={(v) => {
            setContratoOpen(v);
            // Si cierra sin crear contrato, al menos lo llevamos al cliente nuevo
            if (!v) navigate(`/clientes/${clienteNuevoId}`);
          }}
          clientePreselectId={clienteNuevoId}
          initial={{
            latitud: p.lat,
            longitud: p.lng,
            ubicacion_referencia: p.notas ? p.notas.slice(0, 200) : null,
          }}
        />
      )}
    </div>
  );
}
